import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"

import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/lib/supabase"
import i18n from "@/lib/i18n"

const LANGUAGE_STORAGE_KEY = "diagnoza.language"

const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
] as const

export type SupportedLanguageCode = typeof SUPPORTED_LANGUAGES[number]["code"]

interface LocalizationContextValue {
  language: SupportedLanguageCode
  supportedLanguages: typeof SUPPORTED_LANGUAGES
  initializing: boolean
  updating: boolean
  changeLanguage: (language: SupportedLanguageCode) => Promise<void>
}

const LocalizationContext = createContext<LocalizationContextValue | undefined>(undefined)

const isSupportedLanguage = (value?: string | null): value is SupportedLanguageCode => {
  if (!value) return false
  return SUPPORTED_LANGUAGES.some((lang) => lang.code === value)
}

const resolveBrowserLanguage = (): SupportedLanguageCode => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "en"
  }

  const browserLanguage = navigator.language?.split("-")[0]?.toLowerCase()
  if (isSupportedLanguage(browserLanguage)) {
    return browserLanguage
  }

  return "en"
}

export function LocalizationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [language, setLanguage] = useState<SupportedLanguageCode>("en")
  const [initializing, setInitializing] = useState(true)
  const [updating, setUpdating] = useState(false)

  const applyLanguage = useCallback(async (nextLanguage: SupportedLanguageCode) => {
    setLanguage(nextLanguage)

    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem(LANGUAGE_STORAGE_KEY, nextLanguage)
      }
    } catch (error) {
      console.warn("Unable to persist language preference", error)
    }

    if (i18n.language !== nextLanguage) {
      await i18n.changeLanguage(nextLanguage)
    }

    if (typeof document !== "undefined") {
      document.documentElement.lang = nextLanguage
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const initializeLanguage = async () => {
      setInitializing(true)

      let resolvedLanguage: SupportedLanguageCode = resolveBrowserLanguage()

      try {
        if (typeof window !== "undefined") {
          const storedLanguage = window.localStorage?.getItem(LANGUAGE_STORAGE_KEY)
          if (isSupportedLanguage(storedLanguage)) {
            resolvedLanguage = storedLanguage
          }
        }
      } catch {
        // Ignore storage errors
      }

      if (user) {
        try {
          const { data, error } = await supabase
            .from("user_profiles")
            .select("language_preference")
            .eq("user_id", user.id)
            .maybeSingle()

          if (!error && data?.language_preference && isSupportedLanguage(data.language_preference)) {
            resolvedLanguage = data.language_preference
          }
        } catch (error) {
          console.error("Error fetching language preference:", error)
        }
      }

      if (!cancelled) {
        await applyLanguage(resolvedLanguage || "en")
        setInitializing(false)
      }
    }

    void initializeLanguage()

    return () => {
      cancelled = true
    }
  }, [user, applyLanguage])

  const changeLanguage = useCallback(
    async (nextLanguage: SupportedLanguageCode) => {
      if (!isSupportedLanguage(nextLanguage) || nextLanguage === language) {
        return
      }

      setUpdating(true)
      const previousLanguage = language

      try {
        await applyLanguage(nextLanguage)

        if (user) {
          const { error } = await supabase
            .from("user_profiles")
            .update({ language_preference: nextLanguage })
            .eq("user_id", user.id)

          if (error) {
            console.error("Error updating language preference:", error)
            await applyLanguage(previousLanguage)
          }
        }
      } catch (error) {
        console.error("Unable to change language:", error)
        await applyLanguage(previousLanguage)
      } finally {
        setUpdating(false)
      }
    },
    [applyLanguage, language, user]
  )

  const value = useMemo(
    () => ({
      language,
      supportedLanguages: SUPPORTED_LANGUAGES,
      initializing,
      updating,
      changeLanguage,
    }),
    [language, initializing, updating, changeLanguage]
  )

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>
}

export function useLocalization() {
  const context = useContext(LocalizationContext)
  if (!context) {
    throw new Error("useLocalization must be used within a LocalizationProvider")
  }
  return context
}
